import { Box, CloseButton, FloatingHelper, Text, Transition } from '@wix/design-system';
import { useTranslation } from '@wix/wix-i18n-config';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useStore } from 'react-redux';

import { TLocalesKeys } from '../../../../types';
import {
  logChatbotStatusBannerStatusBannerDismissed,
  logChatbotStatusBannerStatusBannerShowed,
} from '../../../bi';
import { useIsAiChatbot } from '../../../common/hooks/useIsAiChatbot';
import { experiments } from '../../../experiments';
import s from './styles.scss';
import { useLocalStorageAnnouncementData } from './useLocalStorageAnnouncementData';

const updatedUIForPartners = experiments.is('specs.chatbot.AiChatbotPartners', 'true');

export const NewAiChatbotAnnouncement: FC = () => {
  const store = useStore();
  const [isClosed, setIsClosed] = useState(true);
  const { t } = useTranslation();
  const title = t<TLocalesKeys>('new_ai_chatbot_announcement_title');
  const text = t<TLocalesKeys>('new_ai_chatbot_announcement_text');

  const { setIsDismissed, getIsDismissed } = useLocalStorageAnnouncementData();
  const isAiChatbot = useIsAiChatbot();

  const bannerBiData = useMemo(
    () => ({
      banner_type: 'announcement',
      banner_name: 'new ai chatbot',
      banner_text: text,
    }),
    [text],
  );

  useEffect(() => {
    const fetchData = async () => {
      const isDismissed = await getIsDismissed();
      setIsClosed(isDismissed);
    };

    // Entry point for showing the banner
    if (isAiChatbot && updatedUIForPartners) {
      fetchData();
    }
  }, [getIsDismissed, isAiChatbot]);

  useEffect(() => {
    if (!isClosed) {
      logChatbotStatusBannerStatusBannerShowed(store.getState(), bannerBiData);
    }
  }, [isClosed, store, bannerBiData]);

  const onClose = useCallback(() => {
    setIsClosed(true);
    logChatbotStatusBannerStatusBannerDismissed(store.getState(), bannerBiData);
    const persistData = async () => {
      await setIsDismissed(true);
    };
    persistData();
  }, [store, bannerBiData, setIsDismissed]);

  const floatingHelperBody = useMemo(
    () => (
      <Text size="small" light>
        {text}
      </Text>
    ),
    [text],
  );

  return (
    <Transition
      show={!isClosed}
      exitAnimation={{
        moveOut: {
          direction: 'bottomToTop',
          distance: '90px',
          duration: 'fast02',
          easing: 'exitEasing',
        },
        fadeOut: {
          duration: 'fast02',
          easing: 'exitEasing',
        },
      }}
    >
      <Box
        padding="SP3 SP4"
        backgroundColor="D10"
        position="relative"
        borderRadius="6px"
        className={s.fadeIn}
        marginBottom="SP1"
      >
        <Box position="absolute" top="6px" right="6px">
          <CloseButton onClick={onClose} skin="light" size="medium" />
        </Box>
        <FloatingHelper.Content title={title} body={floatingHelperBody} />
      </Box>
    </Transition>
  );
};
